import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useContacts } from "../hooks/useContacts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Plus,
  Search,
  UserCircle,
  Building2,
  Mail,
  Phone,
  Loader2,
  FolderOpen,
  Pencil,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Link2,
} from "lucide-react";

import { API_BASE_URL as API } from "../lib/backendUrl";

// US States for dropdown
const US_STATES = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" }, { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" }, { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" }, { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" }, { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" }, { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" }, { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" }, { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" },
];

const getContactTypeBadge = (type) => {
  const styles = {
    client: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    general_contractor: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    subcontractor: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    architect: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
    vendor: "bg-green-500/20 text-green-400 border-green-500/30",
    inspector: "bg-red-500/20 text-red-400 border-red-500/30",
    other: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  };
  return styles[type] || styles.other;
};

const emptyFormData = {
  contact_type: "client",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company: "",
  company_id: "",  // FK to companies table
  title: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  notes: "",
};

const ContactsPage = () => {
  const { contacts, contactTypes, loading, createContact, updateContact, deleteContact } = useContacts();
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [submitting, setSubmitting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "full_name", direction: "asc" });
  const [companies, setCompanies] = useState([]);

  // Fetch companies for dropdown
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await axios.get(`${API}/companies`);
        setCompanies(res.data);
      } catch (error) {
        console.error("Failed to load companies:", error);
      }
    };
    fetchCompanies();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name) {
      toast.error("Please provide first and last name");
      return;
    }
    
    setSubmitting(true);
    try {
      // Prepare data - convert empty company_id to null
      const submitData = {
        ...formData,
        company_id: formData.company_id || null,
      };
      
      if (editingContact) {
        await updateContact(editingContact.id, submitData);
      } else {
        await createContact(submitData);
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      // Error handled in hook
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(emptyFormData);
    setEditingContact(null);
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      contact_type: contact.contact_type,
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email || "",
      phone: contact.phone || "",
      company: contact.company || "",
      company_id: contact.company_id || "",
      title: contact.title || "",
      address_line1: contact.address_line1 || "",
      address_line2: contact.address_line2 || "",
      city: contact.city || "",
      state: contact.state || "",
      postal_code: contact.postal_code || "",
      notes: contact.notes || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Delete ${contact.full_name}? This will remove them from all projects.`)) return;
    await deleteContact(contact.id);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const sortedContacts = useMemo(() => {
    const filtered = contacts.filter((contact) => {
      const matchesSearch =
        contact.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.company?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === "all" || contact.contact_type === typeFilter;
      return matchesSearch && matchesType;
    });

    return [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key] || "";
      const bVal = b[sortConfig.key] || "";
      const comparison = aVal.toString().localeCompare(bVal.toString());
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [contacts, searchQuery, typeFilter, sortConfig]);

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />;
    return sortConfig.direction === "asc" ? 
      <ArrowUp className="h-4 w-4 text-primary" /> : 
      <ArrowDown className="h-4 w-4 text-primary" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="contacts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading">Contacts</h1>
          <p className="text-muted-foreground">Manage contacts across all your projects</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-primary" data-testid="add-contact-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Contact
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] bg-card border-border max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingContact ? "Edit Contact" : "Add New Contact"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Contact Type *</Label>
                  <Select
                    value={formData.contact_type}
                    onValueChange={(v) => setFormData({ ...formData, contact_type: v })}
                  >
                    <SelectTrigger className="bg-muted/50 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {contactTypes.map((ct) => (
                        <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="John"
                    required
                    className="bg-muted/50 mt-1"
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Smith"
                    required
                    className="bg-muted/50 mt-1"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="bg-muted/50 mt-1"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="bg-muted/50 mt-1"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Link2 className="h-3 w-3" />
                    Link to Company
                  </Label>
                  <Select
                    value={formData.company_id || "none"}
                    onValueChange={(v) => {
                      const selectedCompany = companies.find(c => c.id === v);
                      setFormData({ 
                        ...formData, 
                        company_id: v === "none" ? "" : v,
                        // Auto-fill company text field if linking
                        company: selectedCompany ? selectedCompany.name : formData.company
                      });
                    }}
                  >
                    <SelectTrigger className="bg-muted/50 mt-1">
                      <SelectValue placeholder="Select a company" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No linked company</SelectItem>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="flex items-center gap-2">
                            <Building2 className="h-3 w-3" />
                            {c.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Link this contact to an existing company record
                  </p>
                </div>
                <div>
                  <Label>Company Name (Text)</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Or type company name"
                    className="bg-muted/50 mt-1"
                    disabled={!!formData.company_id}
                  />
                  {!formData.company_id && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Use if company doesn't exist in your records
                    </p>
                  )}
                </div>
                <div>
                  <Label>Title / Role</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Project Manager"
                    className="bg-muted/50 mt-1"
                  />
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-sm font-medium text-muted-foreground">Address (Optional)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Street Address</Label>
                    <Input
                      value={formData.address_line1}
                      onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                      placeholder="123 Main Street"
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="New York"
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Select
                      value={formData.state || "none"}
                      onValueChange={(v) => setFormData({ ...formData, state: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="bg-muted/50 mt-1">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select state</SelectItem>
                        {US_STATES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  className="bg-muted/50 mt-1 min-h-[80px]"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-primary">
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {editingContact ? "Updating..." : "Creating..."}
                    </>
                  ) : (
                    editingContact ? "Update Contact" : "Create Contact"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-muted/50"
            data-testid="contact-search"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[200px] bg-muted/50" data-testid="contact-type-filter">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {contactTypes.map((ct) => (
              <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Type Summary */}
      <div className="flex gap-2 flex-wrap">
        {contactTypes.map(ct => {
          const count = contacts.filter(c => c.contact_type === ct.value).length;
          return (
            <Badge
              key={ct.value}
              variant="outline"
              className={`cursor-pointer transition-all ${typeFilter === ct.value ? getContactTypeBadge(ct.value) : "hover:bg-muted"}`}
              onClick={() => setTypeFilter(typeFilter === ct.value ? "all" : ct.value)}
            >
              {ct.label}: {count}
            </Badge>
          );
        })}
      </div>

      {/* Contacts Table */}
      <div className="rounded-lg border border-border/50 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead 
                className="cursor-pointer select-none" 
                onClick={() => handleSort("full_name")}
              >
                <div className="flex items-center gap-2">
                  Name <SortIcon column="full_name" />
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer select-none"
                onClick={() => handleSort("contact_type")}
              >
                <div className="flex items-center gap-2">
                  Type <SortIcon column="contact_type" />
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer select-none"
                onClick={() => handleSort("company")}
              >
                <div className="flex items-center gap-2">
                  Company <SortIcon column="company" />
                </div>
              </TableHead>
              <TableHead>Contact Info</TableHead>
              <TableHead>Projects</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedContacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <UserCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchQuery || typeFilter !== "all"
                      ? "No contacts match your search"
                      : "No contacts yet"}
                  </p>
                  {!searchQuery && typeFilter === "all" && (
                    <Button className="mt-4" onClick={() => setIsDialogOpen(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add First Contact
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              sortedContacts.map((contact) => (
                <TableRow key={contact.id} className="hover:bg-muted/20" data-testid={`contact-row-${contact.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <UserCircle className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <span className="font-medium">{contact.full_name}</span>
                        {contact.title && (
                          <p className="text-xs text-muted-foreground">{contact.title}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getContactTypeBadge(contact.contact_type)}>
                      {contactTypes.find(ct => ct.value === contact.contact_type)?.label || contact.contact_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {contact.company_info ? (
                      <span className="flex items-center gap-1 text-primary">
                        <Link2 className="h-3 w-3" />
                        {contact.company_info.name}
                      </span>
                    ) : contact.company ? (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Building2 className="h-3 w-3" />
                        {contact.company}
                      </span>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3 text-sm">
                      {contact.email && (
                        <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-primary hover:underline">
                          <Mail className="h-3 w-3" />
                          Email
                        </a>
                      )}
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} className="flex items-center gap-1 text-muted-foreground hover:text-primary">
                          <Phone className="h-3 w-3" />
                          {contact.phone}
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {contact.project_count > 0 ? (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <FolderOpen className="h-3 w-3" />
                        {contact.project_count}
                      </span>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEdit(contact)}
                        data-testid={`edit-contact-${contact.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(contact)}
                        data-testid={`delete-contact-${contact.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="text-sm text-muted-foreground">
        Showing {sortedContacts.length} of {contacts.length} contacts
      </div>
    </div>
  );
};

export default ContactsPage;
